import { notifyError } from "../lib/toast";

const BASE = "/api";

export interface RequestOpts {
  /** Skip the global error snackbar for this request — use when the caller already shows its own inline error. */
  silenceErrors?: boolean;
}

async function request<T>(path: string, options: RequestInit = {}, opts?: RequestOpts): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    credentials: "same-origin", // ensures cookies are sent
  });

  if (res.status === 401) {
    // Session expired → redirect to login, preserving where the user was
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?redirect=${redirect}`;
    throw new Error("Not authenticated");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = (body as Record<string, string>).errors || `Request failed: ${res.status}`;
    if (!opts?.silenceErrors) notifyError(message);
    throw new Error(message);
  }

  // Handle 204 No Content
  if (res.status === 204) return undefined as unknown as T;

  return res.json();
}

export function get<T>(path: string, opts?: RequestOpts): Promise<T> {
  return request<T>(path, {}, opts);
}

export function post<T>(path: string, data?: unknown, opts?: RequestOpts): Promise<T> {
  return request<T>(path, {
    method: "POST",
    body: data ? JSON.stringify(data) : undefined,
  }, opts);
}

export function put<T>(path: string, data?: unknown, opts?: RequestOpts): Promise<T> {
  return request<T>(path, {
    method: "PUT",
    body: data ? JSON.stringify(data) : undefined,
  }, opts);
}

export function del<T>(path: string, opts?: RequestOpts): Promise<T> {
  return request<T>(path, { method: "DELETE" }, opts);
}

// Uploads a file as multipart/form-data under the given field name. Omits
// the Content-Type header (unlike the JSON helpers above) so the browser
// can set it itself, boundary included.
export async function postFile<T>(path: string, fieldName: string, file: File, opts?: RequestOpts): Promise<T> {
  const form = new FormData();
  form.append(fieldName, file);

  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    body: form,
    credentials: "same-origin",
  });

  if (res.status === 401) {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?redirect=${redirect}`;
    throw new Error("Not authenticated");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = (body as Record<string, string>).errors || `Request failed: ${res.status}`;
    if (!opts?.silenceErrors) notifyError(message);
    throw new Error(message);
  }

  return res.json();
}

// Fetches a binary response (e.g. a generated file) and triggers a browser download,
// instead of parsing it as JSON like the helpers above.
export async function downloadFile(path: string, filename: string, opts?: RequestOpts): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { credentials: "same-origin" });

  if (res.status === 401) {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?redirect=${redirect}`;
    throw new Error("Not authenticated");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = (body as Record<string, string>).errors || `Request failed: ${res.status}`;
    if (!opts?.silenceErrors) notifyError(message);
    throw new Error(message);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
